// frontend/src/layouts/teams/TeamDetail.js
// -------------------------------------------------------------
// Page "Team Detail" : affichage des membres, ajout/suppression.
// - Ajout multi-membres via Autocomplete (recherche serveur)
// - Respect de la pyramide ED/GA/SUP/PD pour l'ajout
// - Menus (Autocomplete et Select) forçés au-dessus du Dialog
//   grâce au portal + z-index > modal.
// -------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import {
  getTeam,
  listTeamMembers,
  myMembershipForTeam,
  addMembersBulk,
  removeMember,
  searchEligibleUsers,
} from "services/teams";

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Autocomplete,
  Chip,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import MDBox from "components/MDBox";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Niveaux autorisés (pyramide)
const LEVELS = ["ED", "GA", "SUP", "PD"];

/** Cellule Actions (Remove) — composant dédié avec propTypes pour satisfaire ESLint */
function RemoveCell({ row, onRemove }) {
  return (
    <Button size="small" color="error" variant="outlined" onClick={() => onRemove(row.original.id)}>
      Remove
    </Button>
  );
}
RemoveCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default function TeamDetail() {
  const { teamId } = useParams();

  // Données principales
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [myLevel, setMyLevel] = useState(null);
  const [loading, setLoading] = useState(false);

  // Dialog "Add members"
  const [open, setOpen] = useState(false);
  const [roleLevelToAdd, setRoleLevelToAdd] = useState("PD");
  const [saving, setSaving] = useState(false);

  // Autocomplete (recherche côté serveur)
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]); // Résultats proposés [{id, username, email}]
  const [selected, setSelected] = useState([]); // Sélection multiple (mêmes objets)
  const [loadingOptions, setLoadingOptions] = useState(false);

  /** Charge team + membres + mon niveau (whoami pour la team) */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const t = await getTeam(teamId);
      setTeam(t);

      const ms = await listTeamMembers(teamId);
      setMembers(Array.isArray(ms) ? ms : []);

      const mm = await myMembershipForTeam(teamId);
      setMyLevel(mm?.role_level || null);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    load();
  }, [load]);

  /** Vérifie si je peux ajouter ce niveau (pyramide ED > GA > SUP > PD) */
  const canAdd = useCallback(
    (targetLevel) => {
      if (myLevel === "ED") return true; // ED peut tout
      if (myLevel === "GA") return ["SUP", "PD"].includes(targetLevel);
      if (myLevel === "SUP") return targetLevel === "PD";
      return false;
    },
    [myLevel]
  );

  /** Recherche côté serveur des users éligibles NON membres de la team */
  const fetchOptions = useCallback(
    async (q) => {
      setLoadingOptions(true);
      try {
        // Retour attendu: [{ id, username, email }, ...]
        const arr = await searchEligibleUsers(teamId, q, 20);
        setOptions(arr);
      } catch (e) {
        setOptions([]);
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        setLoadingOptions(false);
      }
    },
    [teamId]
  );

  // Déclenche la recherche quand le dialog s'ouvre et à chaque saisie
  useEffect(() => {
    if (open) fetchOptions(query);
  }, [open, query, fetchOptions]);

  /** Ajout en masse (sélection multiple) */
  const onAdd = async () => {
    if (!canAdd(roleLevelToAdd)) return;
    const ids = selected.map((u) => u.id).filter(Boolean);
    if (ids.length === 0) return;

    setSaving(true);
    try {
      await addMembersBulk(teamId, ids, roleLevelToAdd);
      setOpen(false);
      setSelected([]);
      setQuery("");
      await load();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  /** Suppression d'un membre */
  const onRemove = async (membershipId) => {
    try {
      await removeMember(teamId, membershipId);
      await load();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  /** Colonnes du tableau des membres */
  const columns = useMemo(
    () => [
      { Header: "Username", accessor: (r) => r.user?.username ?? "-", align: "left" },
      { Header: "Email", accessor: (r) => r.user?.email ?? "-", align: "left" },
      { Header: "Role Level", accessor: "role_level", align: "center" },
      {
        Header: "Actions",
        accessor: "actions",
        align: "right",
        // ⚠️ Ne pas déstructurer { row } ici : sinon ESLint considère une fonction-composant sans propTypes.
        // On passe par un composant dédié RemoveCell avec ses propTypes.
        Cell: (cell) => <RemoveCell row={cell.row} onRemove={onRemove} />,
      },
    ],
    [onRemove]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={6} pb={3}>
        {/* Header (titre + bouton Add) */}
        <Grid container justifyContent="space-between" alignItems="center" mb={2} px={3}>
          <MDBox>
            <h2 style={{ margin: 0 }}>Team: {team?.name ?? "-"}</h2>
          </MDBox>

          <MDBox>
            {(myLevel === "ED" || myLevel === "GA" || myLevel === "SUP") && (
              <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
                Add member(s)
              </Button>
            )}
          </MDBox>
        </Grid>

        {/* Table membres */}
        <MDBox pt={3}>
          {loading ? (
            <CircularProgress />
          ) : (
            <DataTable table={{ columns, rows: members }} noEndBorder />
          )}
        </MDBox>
      </MDBox>

      <Footer />

      {/* ================= Dialog Add Members (multi) ================ */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add member(s) to team</DialogTitle>

        <DialogContent>
          {/* Autocomplete multiple, recherche serveur
              IMPORTANT : on force le Popper à s'afficher au-dessus du Dialog :
              - disablePortal=false => rendu dans un portail (document.body)
              - slotProps.popper.sx.zIndex > theme.zIndex.modal (≈1300) */}
          <Autocomplete
            multiple
            options={options}
            value={selected}
            onChange={(_, newValue) => setSelected(newValue)}
            onInputChange={(_, newInput) => setQuery(newInput)}
            loading={loadingOptions}
            filterSelectedOptions
            getOptionLabel={(opt) =>
              opt?.username ? `${opt.username}${opt.email ? ` — ${opt.email}` : ""}` : ""
            }
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            // 🔑 Assure que la liste passe DEVANT le Dialog
            disablePortal={false}
            slotProps={{
              popper: {
                sx: { zIndex: (theme) => (theme.zIndex.modal ?? 1300) + 10 }, // 1310+
              },
              paper: {
                sx: { minWidth: 360 }, // Optionnel: largeur mini de la liste
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="normal"
                label="Search users (username/email)"
                placeholder="Type to search..."
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.id} label={option.username} />
              ))
            }
          />

          {/* Sélecteur de niveau (Menu au-dessus du Dialog)
              - MenuProps.container = document.body
              - PaperProps.sx.zIndex > modal */}
          <FormControl fullWidth margin="normal">
            <InputLabel>Role level</InputLabel>
            <Select
              label="Role level"
              value={roleLevelToAdd}
              onChange={(e) => setRoleLevelToAdd(e.target.value)}
              MenuProps={{
                disablePortal: false,
                container: document.body, // force le menu à être en dehors de la hiérarchie du Dialog
                PaperProps: {
                  sx: { zIndex: (theme) => (theme.zIndex.modal ?? 1300) + 10 }, // 1310+
                },
              }}
            >
              {LEVELS.map((lvl) => (
                <MenuItem key={lvl} value={lvl} disabled={!canAdd(lvl)}>
                  {lvl}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={onAdd}
            color="primary"
            variant="contained"
            disabled={saving || selected.length === 0 || !canAdd(roleLevelToAdd)}
          >
            {saving ? "Adding..." : `Add ${selected.length || ""} member(s)`}
          </Button>
        </DialogActions>
      </Dialog>
      {/* ================= /Dialog Add Members ===================== */}
    </DashboardLayout>
  );
}
